import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import {
  collection,
  getCountFromServer,
  getDocs,
  getFirestore,
  query,
  where,
} from 'firebase/firestore';
import { useAdminThemeContext } from '../../contexts/AdminThemeContext';
import { getDeployTarget } from '../../config/environment';

function StatCard({ label, value, subtitle, accent, colors, highlight }) {
  return (
    <View
      style={{
        flex: 1,
        minWidth: 140,
        backgroundColor: colors.cardBg,
        borderRadius: 12,
        padding: 16,
        borderWidth: 1,
        borderColor: highlight ? colors.navActive : colors.cardBorder,
      }}
    >
      <Text style={{ color: colors.textMuted, fontSize: 12, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Text>
      <Text style={{ color: accent || colors.statAccent, fontSize: 32, fontWeight: '800', marginTop: 4, fontFamily: 'VastagoGrotesk' }}>
        {value}
      </Text>
      {subtitle ? (
        <Text style={{ color: colors.textSecondary, fontSize: 12, marginTop: 4 }}>{subtitle}</Text>
      ) : null}
    </View>
  );
}

function QueueRow({ icon, title, count, description, onPress, colors, urgent }) {
  return (
    <TouchableOpacity
      onPress={onPress}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 12,
        paddingHorizontal: 14,
        borderRadius: 10,
        backgroundColor: urgent ? 'rgba(231, 76, 60, 0.08)' : colors.cardBgSubtle,
        borderWidth: 1,
        borderColor: urgent ? 'rgba(231, 76, 60, 0.35)' : colors.cardBorder,
        marginBottom: 8,
      }}
    >
      <View
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          backgroundColor: colors.cardBg,
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: 12,
          borderWidth: 1,
          borderColor: colors.cardBorder,
        }}
      >
        <Ionicons name={icon} size={18} color={urgent ? '#e74c3c' : colors.statAccent} />
      </View>
      <View style={{ flex: 1 }}>
        <Text style={{ color: colors.textPrimary, fontWeight: '700', fontSize: 14 }}>{title}</Text>
        {description ? (
          <Text style={{ color: colors.textSecondary, fontSize: 12, marginTop: 2 }}>{description}</Text>
        ) : null}
      </View>
      <View
        style={{
          minWidth: 28,
          height: 28,
          borderRadius: 14,
          backgroundColor: count > 0 ? (urgent ? '#e74c3c' : colors.navActive) : colors.cardBg,
          alignItems: 'center',
          justifyContent: 'center',
          paddingHorizontal: 8,
          borderWidth: 1,
          borderColor: colors.cardBorder,
        }}
      >
        <Text style={{ color: count > 0 ? colors.textOnAccent : colors.textMuted, fontWeight: '800', fontSize: 13 }}>
          {count}
        </Text>
      </View>
    </TouchableOpacity>
  );
}

function QuickLink({ icon, label, onPress, colors }) {
  return (
    <TouchableOpacity
      onPress={onPress}
      style={{
        flex: 1,
        minWidth: 130,
        backgroundColor: colors.cardBg,
        borderRadius: 10,
        paddingVertical: 14,
        paddingHorizontal: 12,
        borderWidth: 1,
        borderColor: colors.cardBorder,
        alignItems: 'center',
        gap: 6,
      }}
    >
      <Ionicons name={icon} size={22} color={colors.navActive} />
      <Text style={{ color: colors.textPrimary, fontWeight: '600', fontSize: 13, textAlign: 'center' }}>{label}</Text>
    </TouchableOpacity>
  );
}

export function AdminDashboard({
  onSelectTab,
  userStats,
  venueStats,
  newVenueSubmissions = [],
  communityMessages = [],
  maintenanceMode,
  maintenanceList = [],
  pendingVenueUpdates = [],
  adminUser,
}) {
  const { colors, styles: adminStyles } = useAdminThemeContext();
  const [queueLoading, setQueueLoading] = useState(true);
  const [queueCounts, setQueueCounts] = useState({
    venueReports: 0,
    techSpecReports: 0,
    resourceSubmissions: 0,
    documentSubmissions: 0,
    archiveRequests: 0,
    specFlags: 0,
    venueInterestTags: 0,
    verifications: 0,
  });

  const pendingNewVenues = useMemo(
    () => newVenueSubmissions.filter((s) => (s.status || 'pending') === 'pending').length,
    [newVenueSubmissions]
  );

  const photoCoverage = venueStats.total
    ? Math.round((venueStats.withPhotos / venueStats.total) * 100)
    : 0;

  const totalPending = useMemo(
    () =>
      pendingNewVenues +
      queueCounts.venueReports +
      queueCounts.techSpecReports +
      queueCounts.resourceSubmissions +
      queueCounts.documentSubmissions +
      queueCounts.archiveRequests +
      queueCounts.specFlags +
      queueCounts.venueInterestTags +
      queueCounts.verifications +
      pendingVenueUpdates.length,
    [pendingNewVenues, queueCounts, pendingVenueUpdates.length]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const db = getFirestore();
        const [pendingVenueReportsSnap, resourcesSnap, documentsSnap, archiveRequestsSnap, specFlagsSnap, venueInterestTagsSnap, verificationsSnap] = await Promise.all([
          getDocs(query(collection(db, 'venue_reports'), where('status', '==', 'pending'))),
          getCountFromServer(query(collection(db, 'resource_submissions'), where('status', '==', 'pending'))),
          getCountFromServer(query(collection(db, 'venue_document_submissions'), where('status', '==', 'pending'))),
          getCountFromServer(query(collection(db, 'venue_archive_requests'), where('status', '==', 'pending'))),
          getCountFromServer(query(collection(db, 'venue_spec_flags'), where('status', '==', 'pending'))),
          getCountFromServer(query(collection(db, 'venue_interest_tags'), where('status', '==', 'pending'))),
          getCountFromServer(
            query(
              collection(db, 'user_reports'),
              where('type', '==', 'verification'),
              where('status', '==', 'pending')
            )
          ),
        ]);
        let venueReports = 0;
        let techSpecReports = 0;
        pendingVenueReportsSnap.forEach((docSnap) => {
          if (docSnap.data()?.isTechSpecsOnly) techSpecReports += 1;
          else venueReports += 1;
        });
        if (!cancelled) {
          setQueueCounts({
            venueReports,
            techSpecReports,
            resourceSubmissions: resourcesSnap.data().count,
            documentSubmissions: documentsSnap.data().count,
            archiveRequests: archiveRequestsSnap.data().count,
            specFlags: specFlagsSnap.data().count,
            venueInterestTags: venueInterestTagsSnap.data().count,
            verifications: verificationsSnap.data().count,
          });
        }
      } catch (err) {
        console.error('AdminDashboard queue counts', err);
      } finally {
        if (!cancelled) setQueueLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const displayName =
    adminUser?.username ||
    adminUser?.firstName ||
    adminUser?.email?.split('@')[0] ||
    'Admin';

  const deployTarget = getDeployTarget();

  return (
    <View style={{ width: '100%', maxWidth: 1200 }}>
      {/* Welcome + environment */}
      <View style={{ marginBottom: 20 }}>
        <Text style={[adminStyles.pageTitle, { marginBottom: 4 }]}>Dashboard</Text>
        <Text style={adminStyles.pageSubtitle}>
          Welcome back, {displayName}. Here&apos;s what needs attention across Venue Report Card.
        </Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
          <View
            style={{
              paddingHorizontal: 10,
              paddingVertical: 4,
              borderRadius: 6,
              backgroundColor: colors.cardBgSubtle,
              borderWidth: 1,
              borderColor: colors.cardBorder,
            }}
          >
            <Text style={{ color: colors.textSecondary, fontSize: 12 }}>
              Deploy: <Text style={{ fontWeight: '700', color: colors.textPrimary }}>{deployTarget}</Text>
            </Text>
          </View>
          <View
            style={{
              paddingHorizontal: 10,
              paddingVertical: 4,
              borderRadius: 6,
              backgroundColor: maintenanceMode ? 'rgba(231, 76, 60, 0.12)' : 'rgba(46, 204, 113, 0.12)',
              borderWidth: 1,
              borderColor: maintenanceMode ? 'rgba(231, 76, 60, 0.4)' : 'rgba(46, 204, 113, 0.4)',
            }}
          >
            <Text style={{ color: maintenanceMode ? '#e74c3c' : colors.navActive, fontSize: 12, fontWeight: '700' }}>
              {maintenanceMode ? 'Maintenance mode ON' : 'Site live'}
            </Text>
          </View>
          {maintenanceList.length > 0 && (
            <View
              style={{
                paddingHorizontal: 10,
                paddingVertical: 4,
                borderRadius: 6,
                backgroundColor: colors.cardBgSubtle,
                borderWidth: 1,
                borderColor: colors.cardBorder,
              }}
            >
              <Text style={{ color: colors.textSecondary, fontSize: 12 }}>
                {maintenanceList.length} scheduled maintenance window{maintenanceList.length === 1 ? '' : 's'}
              </Text>
            </View>
          )}
        </View>
      </View>

      {/* Stat cards */}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <StatCard
          label="Users"
          value={userStats.total.toLocaleString()}
          subtitle={`${userStats.active} active · ${userStats.admins} admin${userStats.admins === 1 ? '' : 's'}`}
          colors={colors}
        />
        <StatCard
          label="Venues"
          value={venueStats.total.toLocaleString()}
          subtitle={`${photoCoverage}% with photos · ${venueStats.withoutPhotos} need photos`}
          colors={colors}
        />
        <StatCard
          label="Pending review"
          value={queueLoading ? '…' : totalPending.toLocaleString()}
          subtitle="Across all submission queues"
          colors={colors}
          accent={totalPending > 0 ? '#e67e22' : colors.statAccent}
          highlight={totalPending > 0}
        />
        <StatCard
          label="Community msgs"
          value={communityMessages.length.toLocaleString()}
          subtitle="Venue discussion posts"
          colors={colors}
        />
      </View>

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
        {/* Review queue */}
        <View style={{ flex: 1, minWidth: 300 }}>
          <Text style={[adminStyles.cardTitle, { marginBottom: 12 }]}>Review queue</Text>
          {queueLoading ? (
            <ActivityIndicator color={colors.navActive} style={{ marginVertical: 20 }} />
          ) : (
            <>
              <QueueRow
                icon="add-circle-outline"
                title="New venue submissions"
                count={pendingNewVenues}
                description="Venues submitted by users awaiting approval"
                urgent={pendingNewVenues > 0}
                colors={colors}
                onPress={() => onSelectTab('new_venues')}
              />
              <QueueRow
                icon="document-text-outline"
                title="Venue reports"
                count={queueCounts.venueReports}
                description="Grade and experience reports to moderate"
                urgent={queueCounts.venueReports > 0}
                colors={colors}
                onPress={() => onSelectTab('venue_reports', { reportType: 'venue' })}
              />
              <QueueRow
                icon="hardware-chip-outline"
                title="Tech spec reports"
                count={queueCounts.techSpecReports}
                description="LIVE and VRC tech spec submissions awaiting review"
                urgent={queueCounts.techSpecReports > 0}
                colors={colors}
                onPress={() => onSelectTab('venue_reports', { reportType: 'venue', showTechSpecsOnly: true })}
              />
              <QueueRow
                icon="flag-outline"
                title="Spec flags"
                count={queueCounts.specFlags}
                description="LIVE tech spec category notes from venues"
                urgent={queueCounts.specFlags > 0}
                colors={colors}
                onPress={() => onSelectTab('venue_spec_flags', { reportType: 'spec_flag' })}
              />
              <QueueRow
                icon="heart-outline"
                title="Venue interest tags"
                count={queueCounts.venueInterestTags}
                description="Community interest tags submitted for venues"
                urgent={queueCounts.venueInterestTags > 0}
                colors={colors}
                onPress={() => onSelectTab('venue_interest_tags', { reportType: 'venue_interest_tag' })}
              />
              <QueueRow
                icon="link-outline"
                title="Resource submissions"
                count={queueCounts.resourceSubmissions}
                description="Shared gear, docs, and links from the community"
                urgent={queueCounts.resourceSubmissions > 0}
                colors={colors}
                onPress={() => onSelectTab('resource_submissions', { reportType: 'resource_submission' })}
              />
              <QueueRow
                icon="document-attach-outline"
                title="Document uploads"
                count={queueCounts.documentSubmissions}
                description="Venue documents and manuals awaiting approval"
                urgent={queueCounts.documentSubmissions > 0}
                colors={colors}
                onPress={() => onSelectTab('venue_document_submissions', { reportType: 'venue_document_submission' })}
              />
              <QueueRow
                icon="archive-outline"
                title="Venue archive requests"
                count={queueCounts.archiveRequests}
                description="Venues reported as closed or under new ownership"
                urgent={queueCounts.archiveRequests > 0}
                colors={colors}
                onPress={() => onSelectTab('venue_archive_requests', { reportType: 'venue_archive_request' })}
              />
              <QueueRow
                icon="shield-checkmark-outline"
                title="User verifications"
                count={queueCounts.verifications}
                description="Identity verification requests"
                urgent={queueCounts.verifications > 0}
                colors={colors}
                onPress={() => onSelectTab('users')}
              />
              <QueueRow
                icon="cloud-upload-outline"
                title="Tech spec uploads"
                count={pendingVenueUpdates.length}
                description="Pending venue data updates to review"
                urgent={pendingVenueUpdates.length > 0}
                colors={colors}
                onPress={() => onSelectTab('venue_reports', { reportType: 'venue', showTechSpecsOnly: true })}
              />
              <QueueRow
                icon="image-outline"
                title="Venues missing photos"
                count={venueStats.withoutPhotos}
                description="Venues without background or gallery images"
                urgent={false}
                colors={colors}
                onPress={() => onSelectTab('venues')}
              />
            </>
          )}
        </View>

        {/* Quick links */}
        <View style={{ flex: 1, minWidth: 300 }}>
          <Text style={[adminStyles.cardTitle, { marginBottom: 12 }]}>Quick links</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
            <QuickLink icon="business-outline" label="All venues" onPress={() => onSelectTab('venues')} colors={colors} />
            <QuickLink icon="person-outline" label="Users" onPress={() => onSelectTab('users')} colors={colors} />
            <QuickLink icon="chatbubbles-outline" label="Community" onPress={() => onSelectTab('community')} colors={colors} />
            <QuickLink icon="heart-outline" label="Interests" onPress={() => onSelectTab('interests')} colors={colors} />
            <QuickLink icon="mail-outline" label="Notify me" onPress={() => onSelectTab('feature_notify')} colors={colors} />
            <QuickLink icon="settings-outline" label="Operations" onPress={() => onSelectTab('site_operations')} colors={colors} />
            <QuickLink icon="flag-outline" label="Feature flags" onPress={() => onSelectTab('site_operations')} colors={colors} />
            <QuickLink icon="wallet-outline" label="Mutual Fund" onPress={() => onSelectTab('mutual_fund')} colors={colors} />
            <QuickLink icon="cloud-upload-outline" label="Data & imports" onPress={() => onSelectTab('site_data')} colors={colors} />
            <QuickLink icon="map-outline" label="Locations & POIs" onPress={() => onSelectTab('site_locations')} colors={colors} />
          </View>

          <View
            style={{
              marginTop: 20,
              backgroundColor: colors.sectionAccent,
              borderRadius: 12,
              padding: 16,
              borderWidth: 1,
              borderColor: colors.sectionAccentBorder,
            }}
          >
            <Text style={{ color: colors.textPrimary, fontWeight: '700', fontSize: 14, marginBottom: 8 }}>
              Venue health snapshot
            </Text>
            <Text style={{ color: colors.textSecondary, fontSize: 13, lineHeight: 20 }}>
              {venueStats.withPhotos.toLocaleString()} venues have photos, {venueStats.notFound.toLocaleString()} flagged
              not found on LiveNation, and {userStats.organizationAccounts} organization accounts are registered.
            </Text>
            <TouchableOpacity
              onPress={() => onSelectTab('site_data')}
              style={{ marginTop: 12, alignSelf: 'flex-start' }}
            >
              <Text style={{ color: colors.statAccent, fontWeight: '700', fontSize: 13 }}>Open data tools →</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </View>
  );
}
